import { ENV } from 'services/environment/environment';

type PortalTriggerParams = {
  agentName: string;
  partnerId: string;
  partnerName: string;
  userName: string;
  messageId: string;
  locale: string;
  workflowType?: 'enrichment';
};

export const triggerWorkflow = async (params: PortalTriggerParams): Promise<{ ok: true; agentName: string }> => {
  const { agentName, ...body } = params;
  const response = await gruFetch(`/api/agents/${encodeURIComponent(agentName)}/trigger`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ ...body, workflowType: body.workflowType ?? 'enrichment' }),
  });

  if (!response.ok) {
    throw new Error(`Trigger failed: ${response.status}`);
  }

  return (await response.json()) as { ok: true; agentName: string };
};

export const approveActionResult = async (agentName: string, resultId: string): Promise<{ ok: true }> => {
  const response = await gruFetch(
    `/api/agents/${encodeURIComponent(agentName)}/action-results/${encodeURIComponent(resultId)}/approve`,
    {
      method: 'POST',
    },
  );

  if (!response.ok) {
    throw new Error(`Approve failed: ${response.status}`);
  }

  return (await response.json()) as { ok: true };
};

export const retryWorkflow = async (
  agentName: string,
  workflowId: string,
): Promise<{ ok: true; workflowId: string }> => {
  const response = await gruFetch(
    `/api/agents/${encodeURIComponent(agentName)}/workflows/${encodeURIComponent(workflowId)}/retry`,
    { method: 'POST' },
  );

  if (!response.ok) {
    throw new Error(`Retry failed: ${response.status}`);
  }

  return (await response.json()) as { ok: true; workflowId: string };
};

export type WorkflowResponse = {
  workflow: {
    id: string;
    type: string;
    status: string;
    started_at: number;
    updated_at: number;
    applied_at: number | null;
    metadata: string | null;
  };
  actionResults: Array<{
    id: string;
    workflow_id: string;
    tool_name: string;
    minion_name: string;
    status: string;
    input_params: Record<string, unknown> | null;
    payload: Record<string, unknown>;
    visible: number;
    created_at: number;
    updated_at: number;
  }>;
};

export const getWorkflow = async (agentName: string, workflowId: string): Promise<WorkflowResponse> => {
  const response = await gruFetch(
    `/api/agents/${encodeURIComponent(agentName)}/workflows/${encodeURIComponent(workflowId)}`,
  );

  if (!response.ok) {
    throw new Error(`Get workflow failed: ${response.status}`);
  }

  return (await response.json()) as WorkflowResponse;
};

export type WorkflowListItem = {
  id: string;
  type: string;
  status: string;
  started_at: number;
  updated_at: number;
  applied_at: number | null;
  metadata: string | null;
};

export const clearAgent = async (agentName: string): Promise<{ ok: true }> => {
  const response = await gruFetch(`/api/agents/${encodeURIComponent(agentName)}/clear`, {
    method: 'POST',
  });

  if (!response.ok) {
    throw new Error(`Clear failed: ${response.status}`);
  }

  return (await response.json()) as { ok: true };
};

export const listWorkflows = async (agentName: string): Promise<Array<WorkflowListItem>> => {
  const response = await gruFetch(`/api/agents/${encodeURIComponent(agentName)}/workflows`);

  if (!response.ok) {
    throw new Error(`List workflows failed: ${response.status}`);
  }

  return (await response.json()) as Array<WorkflowListItem>;
};

const gruFetch = async (path: string, options?: RequestInit) =>
  fetch(`${ENV.GRU_HOST}${path}`, {
    credentials: 'include',
    ...options,
  });
